import { handleErrors, simpleGetRequest } from "../API/baseAPI";
import eventBus from "../events/eventBus";
import { hasActiveAlerts, moveAlertContainer, showAlert } from "./alerts";

export function getDeviceId() {
    if (window.localStorage) {
        let deviceId = localStorage.getItem('deviceId');
        if (!deviceId) {
            deviceId = self.crypto.randomUUID();
            localStorage.setItem('deviceId', deviceId);
        }
        return deviceId;
    }

    return null;
}

// log out the user
export function logout(res) {
    if (!res) {
        window.location.href = '/account/logout';
        return;
    }

    if (res.error) {
        showAlert(res.message || res.error, 'error');
        return;
    }

    window.location.href = '/account/logout';
}

const scrollPositions = {};

export function getScrollPosition(view) {
    return scrollPositions[view] || 0;
}

export function saveScrollPosition(view, viewId = null) {
    if (viewId) {
        const element = document.getElementById(viewId);
        if (element) {
            scrollPositions[view] = element.scrollTop;
        } else {
            console.error(`Element with id ${viewId} not found.`);
        }
    } else {
        scrollPositions[view] = window.scrollY;
    }
}

export function restoreScrollPosition(view, viewId = null) {
    if (scrollPositions[view] !== undefined) {
        if (viewId) {
            const element = document.getElementById(viewId);
            if (element) {
                element.scrollTo(0, scrollPositions[view]);
            } else {
                console.error(`Element with id ${viewId} not found.`);
            }
        } else {
            window.scrollTo(0, scrollPositions[view]);
        }
    }
}

export function getCurrentUser() {
    return alertUser;
}

export function getUserPreferences() {
    const user = getCurrentUser();

    if (!user) {
        return {};
    }

    if (!user.settings || !user.settings.preferences) {
        return {};
    }

    return user.settings.preferences;
}

export function getHiddenElements() {
    const pref = getUserPreferences();

    if (pref.hide) {
        return pref.hide;
    }

    return [];
}

export function getQueryParams() {
    const searchParams = new URLSearchParams(window.location.search);
    const params = {};
    
    for (const [key, value] of searchParams.entries()) {
        params[key] = value;
    }
    
    return params;
}

export function setQuery(key, value) {
    if (!key || !value || (Array.isArray(key) && !Array.isArray(value)) || (!Array.isArray(key) && Array.isArray(value))) {
        console.error('Invalid key or value provided.');
        return;
    }

    const searchParams = new URLSearchParams(window.location.search);

    searchParams.forEach((value, key) => {
        searchParams.delete(key);
    });

    if (Array.isArray(key) && Array.isArray(value)) {
        for (let i = 0; i < key.length; i++) {
            searchParams.set(key[i], value[i]);
        }
        key = key.join(',');
        value = value.join(',');
    } else {
        searchParams.set(key, value);
    }
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.pushState({ path: newUrl },'setQuery to ' + key + " " + value, newUrl);
}

export function addQuery(key, value) {
    if (key === undefined || value === undefined || (Array.isArray(key) && !Array.isArray(value)) || (!Array.isArray(key) && Array.isArray(value))) {
        console.error('Invalid key or value provided.');
        return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    if (Array.isArray(key) && Array.isArray(value)) {
        for (let i = 0; i < key.length; i++) {
            if (searchParams.has(key[i])) {
                searchParams.delete(key[i]);
            }
            searchParams.append(key[i], value[i]);
        }
        key = key.join(',');
        value = value.join(',');
    } else {
        if (searchParams.has(key)) {
            searchParams.delete(key);
        }
        searchParams.append(key, value);
    }
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.pushState({ path: newUrl }, 'add ' + key + " " + value, newUrl);
}

export function removeQuery(key) {
    const searchParams = new URLSearchParams(window.location.search);
    if (!key || Array.isArray(key) || searchParams.get(key) === null) {
        console.error('Invalid key provided.');
        return;
    }
    searchParams.delete(key);
    if (searchParams.toString() === '') {
        window.history.pushState({}, 'remove ' + key, window.location.pathname);
        return;
    }
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.pushState({ path: newUrl }, 'remove ' + key, newUrl);
}

export function clearQuery() {
    const newUrl = window.location.pathname;
    window.history.pushState({ path: newUrl }, 'clear query', newUrl);
}

/**
 * Checks if an element at a specific depth in the DOM tree has a specific class
 * @param {HTMLElement} element - The starting element
 * @param {number|number[]} path - Either a single number or array of indices to navigate the DOM tree
 * @param {string} className - The class name to check for
 * @param {boolean} [strict=true] - If true, fails if path is invalid; if false, checks as deep as possible
 * @returns {boolean} - True if the target element has the class, false otherwise
 */
export function hasClassAtPath(element, path, className, strict = true) {
    // Parameter validation
    if (!element || typeof className !== 'string' || !element.classList) {
        console.error('Invalid parameters for hasClassAtPath:', { element, path, className });
        return false;
    }
    
    // Convert single number to array for consistent handling
    const pathIndices = Array.isArray(path) ? path : [path];
    
    // Start with the initial element
    let currentElement = element;
    
    // Navigate through the path
    for (let i = 0; i < pathIndices.length; i++) {
        const index = pathIndices[i];
        
        // Special case: index 0 means the element itself
        if (index === 0) {
            continue;
        }
        
        // Validate children exist
        if (!currentElement.children || currentElement.children.length < index) {
            if (strict) {
                return false; // Path is invalid and we're in strict mode
            } else {
                // In non-strict mode, return result for the deepest valid element we found
                break;
            }
        }
        
        // Navigate to the child (1-based index to 0-based array)
        currentElement = currentElement.children[index - 1];
    }
    
    // Check if the final element has the class
    return currentElement.classList.contains(className);
}

/**
 * Checks if any element at a specific depth has a specific class
 * @param {HTMLElement} element - The starting element
 * @param {number} depth - How many levels deep to check (0 means the element itself)
 * @param {string} className - The class name to check for
 * @returns {boolean} - True if any element at specified depth has the class
 */
export function hasClassAtDepth(element, depth, className) {
    if (!element || typeof className !== 'string' || !element.classList || depth < 0) {
        console.error('Invalid parameters for hasClassAtDepth:', { element, depth, className });
        return false;
    }
    
    // Check the element itself if depth is 0
    if (element.classList.contains(className) || depth === 0) {
        return element.classList.contains(className);
    }
    
    // For depth 1, check direct children
    if (depth === 1 || !element.children || element.children.length === 0 || Array.from(element.children).some(child => 
        child.classList.contains(className)
    )) {
        return Array.from(element.children).some(child => 
            child.classList.contains(className)
        );
    }
    
    const checkLevel = function(elements, currentDepth) {
        if (currentDepth === depth || elements.length === 0 || elements.some(el => el.classList.contains(className))) {
            return elements.some(el => el.classList.contains(className));
        }
        
        const children = [];
        elements.forEach(el => {
            children.push(...Array.from(el.children));
        });
        
        if (children.length === 0) {
            return false;
        }
        
        return checkLevel(children, currentDepth + 1);
    };
    
    return checkLevel(Array.from(element.children), 1);
}

// Capitalize the first letter of a string
export function capitalizeFirstLetter(val) {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}

// Lowercase the first letter of a string
export function lowercaseFirstLetter(val) {
    return String(val).charAt(0).toLowerCase() + String(val).slice(1);
}

// Insert a space before capital letters in a string
export function insertSpaceBeforeCapitalLetters(val) {
    return val.replace(/([A-Z])/g, ' $1');
}

export function registerTriggerFunction(modalId, callback) {
    if (!callback || typeof callback != 'function') {
        console.error('Invalid callback function provided.');
        return;
    }

    eventBus.on('modal:open', (data) => {
        if (data.id === modalId) {
            if (callback(data.data) === false) {
                setTimeout(() => {
                    hideModal(modalId);
                }, 100);
            }
        }
    });
}

export function registerCloseFunction(modalId, callback) {
    if (!callback || typeof callback != 'function') {
        console.error('Invalid callback function provided.');
        return;
    }

    eventBus.on('modal:close', (data) => {
        if (data.id === modalId) {
            if (callback(data.data) === false) {
                setTimeout(() => {
                    hideModal(modalId);
                }, 100);
            }
        }
    });
}

export function showModal(modalId, data) {
    const queryParams = getQueryParams();
    if (queryParams.modal === 'false') {
        console.warn(`Modal display is disabled by query parameter.`);
        return;
    }

    eventBus.emit('modal:open', { id: modalId, data });
    openDefaultModal(modalId);
}

function openDefaultModal(modalId) {
    const modal = document.getElementById(modalId);

    if (hasActiveAlerts()) {
        moveAlertContainer(modalId);
    }

    if (modal) {
        if (modal.showModal) {
            addQuery('modal', modalId);
            modal.showModal();
        } else {
            console.error(`The modal ${modalId} does not seem to be setup correctly.`);
        }
        return;
    }
    
    console.error(`The modal ${modalId} does not exist, removing query.`);
    removeQuery('modal');
}

export function hideModal(modalId) {
    const modal = document.getElementById(modalId);

    if (hasActiveAlerts()) {
        moveAlertContainer();
    }

    if (modal) {
        if (modal.close) {
            removeQuery('modal');
            modal.close();
        } else {
            showAlert(`The modal ${modalId} does not seem to be setup correctly.`, 'error');
        }
        eventBus.emit('modal:close', { modalId });
        return;
    }

    showAlert(`The modal ${modalId} does not exist.`, 'error');
}

export function copyToClipboard(text) {
    if (!text) {
        console.error('No text provided.');
        return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    eventBus.emit('clipboard:copy', { text });
}

export function getShortLink(link, spinnerId, id = null, type = null, title = null, description = null, customCode = null, expiresAt = null) {
    if (!link) {
        console.error('No link provided!');
        return;
    }

    simpleGetRequest('/api/v1/link?link=' + encodeURIComponent(link) + '&type=' + type + '&id=' + id + '&title=' + title + '&description=' + description + '&customCode=' + customCode + '&expiresAt=' + expiresAt, spinnerId, (data) => handleShortLink(data, link));
}

export function handleShortLink(res, destination) {
    if (res.error) {
        handleErrors(res);
        return;
    }

    shareLink(res.data.shortUrl, res.data.title, res.data.description, destination);
}

export function shareLink(url, title, description, destination) {
    eventBus.emit('link:shared', { url, title, description, destination });

    if (navigator.share) {
        navigator.share({
            title: title,
            text: description,
            url: url
        }).catch((error) => {
            console.error('Error sharing link:', error);
        });
    } else {
        copyToClipboard(url);
        showAlert('Link copied to clipboard.');
    }
}

// Function to get the color value of a Tailwind CSS class
export function getTailwindColor(className) {
    const tempElement = document.createElement('div');
    tempElement.classList.add(className);
    document.body.appendChild(tempElement);
    const color = getComputedStyle(tempElement).color;
    document.body.removeChild(tempElement);
    return color;
}

// Function to shake an element to indicate an error
export function shakeElement(element) {
    if (!element) {
        console.error('No element provided to shake.');
        return;
    }

    if (typeof element === 'string') {
        element = document.getElementById(element);
    }

    if (!element) {
        console.error('Element not found to shake.');
        return;
    }

    element.classList.add('error-shake');
    setTimeout(() => {
        element.classList.remove('error-shake');
    }, 1000);
}

export function charCounter(inputElement, maxChars = 500) {
    if (!inputElement) {
        console.error('No input element provided for charCounter.');
        return;
    }

    if (typeof inputElement === 'string') {
        inputElement = document.getElementById(inputElement);
    }

    if (!inputElement) {
        console.error('Input element not found for charCounter.');
        return;
    }

    const counterElement = document.createElement('div');
    counterElement.className = 'text-sm text-gray-500 mt-1';
    inputElement.parentNode.insertBefore(counterElement, inputElement.nextSibling);

    const updateCounter = () => {
        const currentLength = inputElement.value.length;
        if (maxChars <= 0) {
            counterElement.innerText = `${currentLength} ${t('u.characters')}`;
            return;
        }
        counterElement.innerText = `${currentLength}/${maxChars} ${t('u.characters')}`;

        if (currentLength > maxChars && maxChars > 0) {
            counterElement.classList.add('text-red-500');
            shakeElement(counterElement);
        } else {
            counterElement.classList.remove('text-red-500');
        }
    };

    inputElement.addEventListener('input', updateCounter);
    updateCounter();
}

export function formatTime(time) {
    if (typeof time == 'long') {
        time = new Date(time);
    }

    const now = new Date();
    const diffInMs = now - time;
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInMonths = Math.floor(diffInDays / 30);

    let timeMessage = '';

    if (diffInMonths > 1) {
        timeMessage = time.toLocaleDateString();
    } else if (diffInDays >= 1) {
        if (diffInDays === 1) {
            timeMessage = t('days.yesterday');
        } else {
            timeMessage = t('days.days', { time: diffInDays });
        }
    } else if (diffInHours >= 1) {
        if (diffInHours === 1) {
            timeMessage = t('days.hour', { time: 'a' });
        } else {
            timeMessage = t('days.hours', { time: diffInHours });
        }
    } else if (diffInMinutes >= 1) {
        if (diffInMinutes === 1) {
            timeMessage = t('days.minute', { time: 'a' });
        } else {
            timeMessage = t('days.minutes', { time: diffInMinutes });
        }
    } else {
        timeMessage = t('days.now');
    }

    return timeMessage;
}

export function isTruncated(element, wrapperElement, bodyElement) {
    requestAnimationFrame(() => {
        // Give browser time to complete layout
        setTimeout(() => {
            // Check if text is actually truncated (with 2px tolerance for rounding)
            const isTextTruncated = element.scrollWidth > element.clientWidth + 2 || element.scrollHeight > element.clientHeight + 2;
            if (isTextTruncated) {
                const readMore = document.createElement('span');
                readMore.classList.add('text-primary', 'cursor-pointer');
                readMore.innerText = t('u.readMore');
        
                const showLess = document.createElement('span');
                showLess.classList.add('text-primary', 'cursor-pointer', 'hidden');
                showLess.innerText = t('u.showLess');
                let truncated = false;
                let lineClamp = false;
                readMore.addEventListener('click', () => {
                    if (element.classList.contains('line-clamp')) {
                        element.classList.remove('line-clamp');
                        lineClamp = true;
                    }
                    if (element.classList.contains('truncate')) {
                        element.classList.remove('truncate');
                        truncated = true;
                    }
                    readMore.classList.add('hidden');
                    showLess.classList.remove('hidden');
                    bodyElement.classList.add('lg:h-auto');
                });
        
                showLess.addEventListener('click', () => {
                    if (lineClamp) {
                        element.classList.add('line-clamp');
                    }
                    if (truncated) {
                        element.classList.add('truncate');
                    }
                    readMore.classList.remove('hidden');
                    showLess.classList.add('hidden');
                    bodyElement.classList.remove('lg:h-auto');
                });
        
                wrapperElement.appendChild(readMore);
                wrapperElement.appendChild(showLess);
            }
        }, 50);
    });
}

export function isValidHttpUrl(str) {
    try {
        const url = new URL(str);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
}

export function ensureUrl(value) {
    if (!value) return null;
    if (isValidHttpUrl(value)) return value;
    return `https://${value}`;
}

export function socialLink(platform, value) {
    if (!value || typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;

    // If user already provided a URL, keep it.
    if (isValidHttpUrl(trimmed)) return trimmed;

    const handle = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;

    switch (platform) {
        case 'instagram':
            return `https://instagram.com/${handle}`;
        case 'tiktok':
            return `https://www.tiktok.com/@${handle}`;
        case 'twitter':
            return `https://x.com/${handle}`;
        case 'facebook':
            return ensureUrl(trimmed);
        case 'whatsapp': {
            const digits = trimmed.replace(/[^\d+]/g, '');
            if (!digits) return null;
            return `https://wa.me/${digits.replace('+', '')}`;
        }
        default:
            return ensureUrl(trimmed);
    }
}

export function appendLink(label, href, iconClass) {
    if (!href) return;
    const row = document.createElement('span');
    const safeHref = ensureUrl(href) || href;
    row.innerHTML = `${iconClass ? `<i class="${iconClass} mr-1"></i>` : ''}${label}: <a href="${safeHref}" class="link link-primary" target="_blank" rel="noopener noreferrer">${href}</a>`;
    additionalInfo.appendChild(row);
}

export function debounce(func, delay) {
    let timeoutId;

    return function debouncedFunction(...args) {
        const context = this;
        
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            func.apply(context, args);
        }, delay);
    };
}

export function isLocal() {
    return window.location.hostname == 'localhost';
}

export function getDfBHost() {
    if (isLocal()) {
        return 'http://localhost:3003';
    } else {
        return 'https://diefettebrxnette.xyz'
    }
}

export function getReviewHost() {
    if (isLocal()) {
        return 'http://localhost:3004';
    } else {
        return 'https://dönerli.net'
    }
}

export function getService() {
    if (isLocal()) {
        if (window.location.port == '3003') {
            return 'diefettebrxnette';
        }
        return 'dönerli';
    } else {
        if (window.location.hostname == 'diefettebrxnette.xyz') {
            return 'diefettebrxnette'
        }
        return 'dönerli'
    }
}

export function isAdmin() {
    if (!alertUser) {
        return false;    
    }

    if (alertUser.types && (alertUser.types.includes('Admin') || alertUser.types.includes('admin'))) {
        return true;
    }

    return false;
}

export function isReviewer() {
    if (!alertUser) {
        return false;
    }

    if (alertUser.types && (alertUser.types.includes('Reviewer') || alertUser.types.includes('reviewer'))) {
        return true;
    }

    return false;
}

export function isDarkMode() {
    const currentTheme = document.documentElement.getAttribute('data-theme') || 'light';

    return currentTheme === 'dark';
}